'use client';

// @spec developer-tools
import { c as _c } from "react/compiler-runtime";
import { setRumLogger } from '@fpo3/famfam-rum/rum-logger';
import { ClientLogger, HttpTransport } from '@fpo3/ts-commons-observability/log-tunnel';
import { type ReactNode, useEffect } from 'react';

/**
 * Singleton HTTP client logger instance.
 */
export const clientLogger = new ClientLogger({
  transport: new HttpTransport({
    endpoint: (process.env.NEXT_PUBLIC_BASE_PATH || '') + '/api/dev/client-logs'
  })
});

// Route oversized-payload warnings emitted by `@fpo3/famfam-rum`
// (`recordRumAction` payload filter) through the client log tunnel. Wired
// at module top-level so action calls dispatched before `RumProvider`
// mounts (sign-in flow, early reactor effects) still report into the
// configured sink rather than the default `console`.
setRumLogger(clientLogger);
interface LogTunnelProviderProps {
  children: ReactNode;
}

/**
 * Provider that enables client log tunneling to the server via HTTP.
 * Only active in development mode.
 *
 * Once mounted, the singleton `clientLogger` can be imported and used directly
 * from any client code:
 *
 * **Production behavior:** In production (or any context where `LogTunnelProvider`
 * has not been mounted, such as error boundary pages), log entries are echoed to
 * the browser console (`logToConsole` defaults to `true`) but are NOT sent to the
 * server. The `/api/dev/client-logs` endpoint is blocked in production by `proxy.ts`.
 *
 * @example
 * ```tsx
 * import { clientLogger } from '@client/log-tunnel-provider';
 *
 * // In any client code (components, hooks, services)
 * clientLogger.log('Something happened', { data: 123 });
 * clientLogger.error('Failed to load', { error: err.message });
 * ```
 */
export function LogTunnelProvider(t0) {
  const $ = _c(1);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp2, t1);
  return children;
}
function _temp2() {
  if (process.env.NODE_ENV !== "development") {
    return;
  }
  clientLogger.connect();
  return _temp;
}
function _temp() {
  clientLogger.disconnect();
}